import { Site } from '@ps/web-core-enums';
import { FeatureFlag } from '@ps/web-core-store';
import { StarsInfo } from '@ps/web-shared-components';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import type { FormRenderProps } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { styles as sharedStyles } from '@swf/account';
import createForm from '@swf/components/Forms/Form';
import StarsButton from '@swf/components/StarsButton/StarsButton';
import Translation from '@swf/components/Translation/Translation';
import { DeAddressVerificationResult } from '@swf/core/enums';
import { ErrorCode } from '@swf/core/enums/errorCode';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';
import type { Response } from '@swf/core/types/Account';
import type { TrackedTranslation } from '@swf/core/types/i18n';
import { scrollToTop } from '@swf/core/utils';
import { buildFormErrors } from '@swf/core/utils/forms';
import countries from '@swf/generated/countries.json';

import ErrorResponseMessage from '../ErrorResponseMessage/ErrorResponseMessage';

import {
  TXTCLI_Apply__forChanges,
  TXTCLI_Something_went_wrongX_please_t_ELL,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format,
  TXTWCL_The_city_you_have_entered_is_i_ELL_1
} from './AddressFormLayout.messages.json';

export interface AddressFormValues {
  streetAddress?: string;
  city?: string;
  state: string;
  zip: string;
  country: string;
  streetName?: string;
  buildingNumber?: string;
  orientationNumber?: string;
  region?: string;
  district?: string;
  partOfMunicipality?: string;
  extAuthToken?: string;
  [key: string]: string | undefined;
}

interface Props {
  successCallback?: () => void;
  submitButtonText?: TrackedTranslation;
  children: (
    formProps: FormRenderProps<AddressFormValues, Partial<AddressFormValues>>,
    submitButton: React.ReactNode
  ) => React.ReactNode;
  onUpdate: (formValues: AddressFormValues) => Promise<Response | void>;
  initialData?: AddressFormValues;
  submitButtonBlock?: boolean;
}

const Form = createForm<AddressFormValues>();

const errorTranslations = {
  TXTWCL_The_city_you_have_entered_is_i_ELL_1,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_Wrong_Zip_format
};

const AddressFormLayout: React.FC<Props> = ({
  successCallback,
  submitButtonText = TXTCLI_Apply__forChanges,
  children,
  onUpdate,
  initialData,
  submitButtonBlock = true
}) => {
  const site = useSelector(select.cms.getSiteId);
  const country = useSelector(select.account.getCountry) as string;
  const euDeOasisStreetAddress = useFeature(FeatureFlag.EuDeOasisStreetAddress);
  const streetAddress = useSelector(select.account.getAddress);
  const buildingNumber = useSelector(select.account.getBuildingNumber);
  const streetName = useSelector(select.account.getStreetName);
  const city = useSelector(select.account.getCity);
  const zip = useSelector(select.account.getZipCode);
  const state = useSelector(select.account.getAddressState) as string;
  const orientationNumber = useSelector(select.account.getOrientationNumber);
  const district = useSelector(select.account.getDistrict);
  const partOfMunicipality = useSelector(select.account.getPartOfMunicipality);
  const { push } = useHistory();
  const isSiteCZ = site === Site.CZ;

  const onSubmit = (values: AddressFormValues) =>
    onUpdate({
      ...values,
      country,
      streetAddress:
        euDeOasisStreetAddress || isSiteCZ
          ? `${values.streetName} ${values.buildingNumber}`
          : values.streetAddress
    }).then((response) => {
      scrollToTop();

      if (response) {
        if (response.errors?.beError) {
          push('/notifications/egamesng-notification', {
            belgianRegulatorError: response.errors.beError
          });
          return;
        }

        const errors = buildFormErrors(response, errorTranslations);

        if (response.deAddressVerificationResult === DeAddressVerificationResult.Failed) {
          return { ...errors, [FORM_ERROR]: <ErrorResponseMessage /> };
        } else if (response.errors?.code === ErrorCode.ERR_LUGAS_REQUEST_FAILED) {
          return {
            [FORM_ERROR]: (
              <StarsInfo type="error" className={sharedStyles.message}>
                <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />{' '}
              </StarsInfo>
            )
          };
        } else if (errors?.[FORM_ERROR]) {
          return {
            ...errors,
            [FORM_ERROR]: (
              <StarsInfo type="error" className={sharedStyles.message}>
                {errors?.[FORM_ERROR]}
              </StarsInfo>
            )
          };
        } else if (successCallback && !errors) {
          successCallback();
          return;
        }

        return errors;
      }
    });

  const initialCountry = (countries as { value: string; text: string }[]).find(
    ({ value }) => value === country
  )?.text;

  const initialFormValues: AddressFormValues = {
    streetAddress,
    streetName,
    buildingNumber,
    city,
    zip: zip || '',
    state,
    country: initialCountry || '',
    orientationNumber,
    district,
    partOfMunicipality
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialData || initialFormValues}
      render={(formProps) => (
        <>
          {children(
            formProps,
            <StarsButton
              block={submitButtonBlock}
              loading={formProps.submitting}
              disabled={
                formProps.pristine || (formProps.hasSubmitErrors && !formProps.dirtySinceLastSubmit)
              }
              type="submit"
              className={sharedStyles.submit}
              translation={submitButtonText}
            />
          )}
        </>
      )}
    />
  );
};

export default AddressFormLayout;
